import React, { useState } from "react";
import { Modal, Button, Typography, Space, Alert, message } from "antd";
import { WarningOutlined, LockOutlined } from "@ant-design/icons";
import { useAgentBillingStatus } from "../../../contexts/AgentBillingStatusContext";
import { startAgentBillingSetup } from "../../../services/agentBillingStatusApi";

const { Title, Paragraph, Text } = Typography;

/**
 * פופאפ אכיפת Billing Migration.
 *
 * מוצג ברמת ה-layout של הדשבורד (AgentDashboardChrome). מבוסס על
 * AgentBillingStatusContext:
 *
 *   - פעם אחת ב-session: נפתח כאשר `shouldOpenMigrationModal` נכון.
 *   - ב-isBlocked = true: נפתח תמיד, בלי אפשרות סגירה (escape / outside-click / X).
 *   - כפתור ראשי: "הסדרת אמצעי תשלום" — קורא ל-POST /api/agent/billing/setup
 *     ומפנה את המשתמש ל-HYP (`setupUrl`).
 *   - כפתור משני (רק לפני חסימה): "המשך למערכת" — קורא ל-POST /billing/defer.
 *
 * הערה: ה-modal הוא מנגנון אכיפה, לא באנר; הוא יושב ברמת layout
 * ולא בתוך מערכת ההתראות/הבאנרים. זה מבטיח שגם ניווט ידני ב-URL
 * לא עוקף את החוויה.
 */

const COPY = {
  defer: {
    title: "נדרשת הסדרת אמצעי תשלום",
    body:
      "כדי להמשיך להשתמש במערכת באופן מלא, יש להסדיר אמצעי תשלום. " +
      "ניתן להמשיך כעת למערכת, אך לאחר 4 דחיות הגישה תוגבל עד להסדרת התשלום.",
  },
  blocked: {
    title: "הגישה הוגבלה עד להסדרת אמצעי תשלום",
    body: "כדי להמשיך להשתמש במערכת, יש להסדיר כעת אמצעי תשלום.",
  },
  primaryCta: "הסדרת אמצעי תשלום",
  primaryCtaLoading: "פותח הסדרת תשלום…",
  secondaryCta: "המשך למערכת",
  setupErrorGeneric: "שגיאה בפתיחת הסדרת התשלום. נסה שוב.",
  setupErrorNotLegacy:
    "מצב המנוי עודכן. המסך ירוענן אוטומטית.",
};

export default function BillingMigrationGateModal() {
  const {
    shouldOpenMigrationModal,
    isBlocked,
    deferCount,
    maxDefers,
    defer,
    deferring,
    reload,
  } = useAgentBillingStatus();
  const [openingSetup, setOpeningSetup] = useState(false);

  if (!shouldOpenMigrationModal) {
    return null;
  }

  const copy = isBlocked ? COPY.blocked : COPY.defer;
  const remaining = Math.max(0, Number(maxDefers || 0) - Number(deferCount || 0));

  const handleOpenBillingSetup = async () => {
    // מונע double-click: הכפתור מושבת ב-loading וה-flag openingSetup.
    if (openingSetup) return;
    setOpeningSetup(true);
    try {
      const payload = await startAgentBillingSetup();
      const setupUrl = payload?.setupUrl || payload?.redirectUrl;
      if (!setupUrl) {
        message.error(COPY.setupErrorGeneric);
        setOpeningSetup(false);
        return;
      }
      // Redirect מלא ל-HYP. ה-callback יחזור ל-/api/payment-methods/hyp/success
      // שמעדכן payment_methods=active ומפעיל finalize_legacy_migration, וה-FE
      // יקרא GET /billing/status שוב מייד אחרי ה-return ויעדכן את המצב.
      window.location.href = setupUrl;
    } catch (err) {
      // נסיון fallback: אולי המצב כבר השתנה ברקע.
      const serverCode = err?.response?.data?.code;
      if (serverCode === "not_legacy_unbilled") {
        message.info(COPY.setupErrorNotLegacy);
        try {
          await reload();
        } catch {
          /* ignore */
        }
      } else {
        message.error(COPY.setupErrorGeneric);
      }
      setOpeningSetup(false);
    }
    // אם ה-redirect הצליח, הדף עוזב ואין צורך לאפס state.
  };

  const handleDeferAndContinue = async () => {
    try {
      await defer();
    } catch {
      /* swallow — שגיאה לא מסגירה את ה-modal; המשתמש יכול לנסות שוב */
    }
  };

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      onCancel={undefined}
      width={520}
      aria-modal="true"
      aria-labelledby="hr-billing-migration-modal-title"
      aria-describedby="hr-billing-migration-modal-body"
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space align="center" size="middle">
          {isBlocked ? (
            <LockOutlined style={{ fontSize: 28, color: "#d4380d" }} />
          ) : (
            <WarningOutlined style={{ fontSize: 28, color: "#d48806" }} />
          )}
          <Title
            id="hr-billing-migration-modal-title"
            level={4}
            style={{ margin: 0 }}
          >
            {copy.title}
          </Title>
        </Space>

        <Paragraph
          id="hr-billing-migration-modal-body"
          style={{ margin: 0, lineHeight: 1.7 }}
        >
          {copy.body}
        </Paragraph>

        {!isBlocked && Number(maxDefers) > 0 ? (
          <Alert
            type={remaining <= 1 ? "warning" : "info"}
            showIcon
            message={
              <Text>
                נותרו {remaining} דחיות מתוך {maxDefers}.
              </Text>
            }
          />
        ) : null}

        <Space
          direction="horizontal"
          size="small"
          style={{ width: "100%", justifyContent: "flex-end" }}
          wrap
        >
          {!isBlocked ? (
            <Button
              onClick={handleDeferAndContinue}
              loading={deferring}
              disabled={deferring || openingSetup}
            >
              {COPY.secondaryCta}
            </Button>
          ) : null}
          <Button
            type="primary"
            onClick={handleOpenBillingSetup}
            loading={openingSetup}
            disabled={openingSetup}
          >
            {openingSetup ? COPY.primaryCtaLoading : COPY.primaryCta}
          </Button>
        </Space>
      </Space>
    </Modal>
  );
}
